import { styled, keyframes } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';

// ============================================
// Shell
// ============================================
export const EditModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '1112px',
  maxWidth: 'calc(100vw - 48px)',
  height: '680px',
  maxHeight: 'calc(100vh - 48px)',
  backgroundColor: '#fff',
  borderRadius: '10px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  outline: 'none',
});

export const EditHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px',
  height: '52px',
  backgroundColor: '#262626',
  flexShrink: 0,
});

export const EditHeaderTitle = styled(Typography)({
  fontSize: '17px',
  fontWeight: 600,
  color: '#fff',
});

export const EditHeaderClose = styled(Box)({
  cursor: 'pointer',
  color: '#fff',
  fontSize: '20px',
  lineHeight: 1,
  fontWeight: 300,
  transition: 'opacity 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': { opacity: 0.8 },
  '&:active': { transform: 'scale(0.9)' },
});

export const EditBody = styled(Box)({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  padding: '16px',
  backgroundColor: '#e4e8eb',
  position: 'relative',
});

// ============================================
// Panels
// ============================================
export const PoolPanel = styled(Box)({
  width: '480px',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#fff',
  border: '1px solid #d6dbdf',
  borderRadius: '10px',
  overflow: 'hidden',
  boxShadow: '0 2px 6px rgba(28,38,46,0.08)',
});

export const PanelGap = styled(Box)({ width: '16px', flexShrink: 0 });

export const PickedPanel = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#fff',
  border: '1px solid #9fd4d9',
  borderRadius: '10px',
  overflow: 'hidden',
  boxShadow: '0 2px 6px rgba(28,38,46,0.08)',
});

// ============================================
// Search / toolbar
// ============================================
export const SearchRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 16px',
  borderBottom: '1px solid #eceff1',
});

export const SearchField = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  height: '38px',
  padding: '0 10px 0 12px',
  border: '1px solid #d9d9d9',
  borderRadius: '8px',
  gap: '8px',
  '& input': {
    flex: 1,
    minWidth: 0,
    border: 'none',
    outline: 'none',
    fontSize: '13.5px',
    fontWeight: 500,
    color: '#262626',
    background: 'transparent',
  },
});

export const FilterToggleButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  cursor: 'pointer',
  height: '38px',
  padding: '0 12px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '12.5px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  flexShrink: 0,
  border: `1px solid ${active ? '#4ab3bc' : '#d9d9d9'}`,
  backgroundColor: active ? '#e6f4f5' : '#fff',
  color: active ? '#2b7e85' : '#595959',
}));

export const ResultRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 16px',
  borderBottom: '1px solid #eceff1',
});

export const ResultLabel = styled(Typography)({
  fontSize: '12px',
  fontWeight: 600,
  color: '#8c8c8c',
  whiteSpace: 'nowrap',
});

export const AddAllButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'disabled',
})(({ disabled }) => ({
  cursor: disabled ? 'default' : 'pointer',
  height: '28px',
  padding: '0 11px',
  borderRadius: '6px',
  backgroundColor: disabled ? '#cfd5d9' : '#4ab3bc',
  color: '#fff',
  fontSize: '12px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  transition: 'background-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': { backgroundColor: disabled ? '#cfd5d9' : '#3a9fa8' },
  '&:active': { transform: disabled ? 'none' : 'scale(0.97)' },
}));

// ============================================
// Rows
// ============================================
export const ListHead = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 16px',
  backgroundColor: '#f7f8fa',
  borderBottom: '1px solid #eceff1',
  fontSize: '11px',
  fontWeight: 700,
  color: '#8c8c8c',
});

export const ListScroll = styled(Box)({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-thumb': { background: '#d9d9d9', borderRadius: '2px' },
});

export const PoolRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'odd',
})(({ odd }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 16px',
  cursor: 'pointer',
  backgroundColor: odd ? '#fafbfb' : '#fff',
  '&:hover': { backgroundColor: '#f0f8f9' },
}));

export const PickedRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'odd',
})(({ odd }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 20px',
  backgroundColor: odd ? '#fafbfb' : '#fff',
}));

export const RowCheck = styled(Box, {
  shouldForwardProp: prop => prop !== 'checked',
})(({ checked }) => ({
  width: '16px',
  height: '16px',
  borderRadius: '3px',
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '10px',
  fontWeight: 800,
  color: '#fff',
  border: `1.5px solid ${checked ? '#4ab3bc' : '#c4c9cc'}`,
  backgroundColor: checked ? '#4ab3bc' : '#fff',
}));

export const Cell = styled(Box, {
  shouldForwardProp: prop => !['w', 'grow', 'right', 'center'].includes(prop),
})(({ w, grow, right, center }) => ({
  ...(grow ? { flex: 1, minWidth: 0 } : { width: w, flexShrink: 0 }),
  fontSize: '12.5px',
  color: '#737373',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  ...(right ? { textAlign: 'right' } : {}),
  ...(center ? { textAlign: 'center' } : {}),
}));

// ListHead 안의 라벨. Cell이 본문용 폰트를 직접 지정하므로 헤더는 따로 덮어쓴다.
export const HeadCell = styled(Cell)({
  fontSize: '11px',
  fontWeight: 700,
  color: '#8c8c8c',
  textAlign: 'center',
});

export const NameCell = styled(Cell)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#262626',
});

export const TypeBadge = styled(Box, {
  shouldForwardProp: prop => prop !== 'tone',
})(({ tone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  height: '19px',
  padding: '0 7px',
  borderRadius: '10px',
  fontSize: '10.5px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  backgroundColor: `${tone}18`,
  border: `1px solid ${tone}40`,
  color: tone,
}));

// w/grow/right는 Cell이 직접 해석하므로 여기서 걸러내면 안 된다.
export const ConsentText = styled(Cell, {
  shouldForwardProp: prop => prop !== 'agreed',
})(({ agreed }) => ({
  fontSize: '11px',
  fontWeight: 600,
  color: agreed ? '#737373' : '#C2403D',
}));

// 엑셀 업로드 버튼 — 엑셀 상징색(초록) 유지
export const ExcelAddButton = styled(Box)({
  height: '32px',
  padding: '0 11px',
  borderRadius: '6px',
  fontSize: '11.5px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  cursor: 'pointer',
  color: '#1E7145',
  background: 'transparent',
  border: '1px solid #CDe8da',
  transition: 'background-color 150ms ease, border-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': { background: '#F2FAF5', borderColor: '#9FD4B8' },
  '&:active': { transform: 'scale(0.97)' },
});

export const RemoveRowButton = styled(Box)({
  cursor: 'pointer',
  flexShrink: 0,
  width: '22px',
  height: '22px',
  borderRadius: '5px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#a6a6a6',
  fontSize: '13px',
  transition: 'background-color 150ms ease, color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': { backgroundColor: '#fdf3f1', color: '#C2403D' },
  '&:active': { transform: 'scale(0.9)' },
});

export const EmptyMessage = styled(Box)({
  padding: '32px 12px',
  textAlign: 'center',
  fontSize: '13px',
  fontWeight: 500,
  color: '#a6a6a6',
});

// ============================================
// Picked panel header
// ============================================
export const PickedHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 16px',
  borderBottom: '1px solid #eceff1',
});

export const PickedTitle = styled(Typography)({
  fontSize: '14.5px',
  fontWeight: 700,
  color: '#262626',
  whiteSpace: 'nowrap',
  '& span': { color: '#4ab3bc', fontSize: '16.5px' },
});

export const PickedSearchField = styled(Box)({
  flex: 1,
  minWidth: '80px',
  display: 'flex',
  alignItems: 'center',
  height: '32px',
  padding: '0 8px 0 10px',
  border: '1px solid #e3e6e8',
  borderRadius: '6px',
  gap: '6px',
  '& input': {
    flex: 1,
    minWidth: 0,
    border: 'none',
    outline: 'none',
    fontSize: '12.5px',
    fontWeight: 500,
    color: '#262626',
    background: 'transparent',
  },
});

export const ExcludeButton = styled(Box, {
  shouldForwardProp: prop => prop !== 'enabled',
})(({ enabled }) => ({
  height: '32px',
  padding: '0 11px',
  borderRadius: '6px',
  fontSize: '11.5px',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  cursor: enabled ? 'pointer' : 'default',
  border: `1px solid ${enabled ? '#e6b8b0' : '#e6e9eb'}`,
  backgroundColor: enabled ? '#fff' : '#f7f8fa',
  color: enabled ? '#8a3524' : '#b6bcc0',
  transition: 'border-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': { borderColor: enabled ? '#d8a49a' : '#e6e9eb' },
  '&:active': { transform: enabled ? 'scale(0.97)' : 'none' },
}));

// ============================================
// Filter overlay panel
// ============================================
export const FilterPanel = styled(Box)({
  position: 'absolute',
  left: '16px',
  top: '16px',
  bottom: '16px',
  width: '480px',
  zIndex: 9,
  backgroundColor: '#fff',
  border: '1px solid #cfd5d9',
  borderRadius: '10px',
  boxShadow: '0 10px 28px rgba(28,38,46,0.18)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

export const FilterPanelHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
  padding: '12px 16px 11px',
  borderBottom: '1px solid #eceff1',
  flexShrink: 0,
});

export const FilterPanelTitle = styled(Typography)({
  fontSize: '13.5px',
  fontWeight: 700,
  color: '#262626',
  whiteSpace: 'nowrap',
});

export const FilterPanelClose = styled(Box)({
  cursor: 'pointer',
  width: '24px',
  height: '24px',
  borderRadius: '5px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#a6a6a6',
  fontSize: '14px',
  transition: 'background-color 150ms ease, color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': { backgroundColor: '#f5f7f8', color: '#262626' },
  '&:active': { transform: 'scale(0.9)' },
});

export const FilterPanelBody = styled(Box)({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '18px',
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-thumb': { background: '#d9d9d9', borderRadius: '2px' },
});

export const FilterSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
});

export const FilterSectionTitle = styled(Typography)({
  display: 'flex',
  alignItems: 'baseline',
  marginBottom: '8px',
  fontSize: '11.5px',
  fontWeight: 700,
  letterSpacing: '0.2px',
  color: '#8c8c8c',
  whiteSpace: 'nowrap',
});

export const ChipRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
});

export const StopDateHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

export const StopDateRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const FilterChip = styled(Box, {
  shouldForwardProp: prop => prop !== 'active',
})(({ active }) => ({
  cursor: 'pointer',
  height: '30px',
  padding: '0 12px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  fontSize: '12.5px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  border: `1px solid ${active ? '#4ab3bc' : '#d9d9d9'}`,
  backgroundColor: active ? '#4ab3bc' : '#fff',
  color: active ? '#fff' : '#595959',
}));

export const FilterPanelFooter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '12px 16px',
  borderTop: '1px solid #eceff1',
  flexShrink: 0,
});

export const FilterResetButton = styled(Box)({
  cursor: 'pointer',
  height: '38px',
  padding: '0 14px',
  borderRadius: '8px',
  border: '1px solid #e0e3e5',
  backgroundColor: '#fff',
  color: '#737373',
  fontSize: '13px',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  transition: 'border-color 150ms ease, color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': { borderColor: '#c9ced1', color: '#262626' },
  '&:active': { transform: 'scale(0.97)' },
});

export const FilterApplyButton = styled(Box)({
  cursor: 'pointer',
  flex: 1,
  height: '38px',
  borderRadius: '8px',
  backgroundColor: '#4ab3bc',
  color: '#fff',
  fontSize: '13.5px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  whiteSpace: 'nowrap',
  transition: 'background-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': { backgroundColor: '#3a9fa8' },
  '&:active': { transform: 'scale(0.97)' },
});

// ============================================
// Footer
// ============================================
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const UndoBar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '8px 24px',
  backgroundColor: '#262626',
  flexShrink: 0,
  animation: `${slideUp} 0.18s ease-out`,
});

export const UndoLabel = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  color: '#fff',
});

export const UndoAction = styled(Box)({
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 700,
  color: '#7fd6dd',
  whiteSpace: 'nowrap',
  '&:hover': { textDecoration: 'underline' },
});

export const FlashBar = styled(Box)({
  padding: '8px 24px',
  backgroundColor: '#e6f4f5',
  borderTop: '1px solid #b8e0e3',
  fontSize: '12px',
  fontWeight: 700,
  color: '#2b7e85',
  flexShrink: 0,
});

export const EditFooter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '0 20px',
  height: '64px',
  borderTop: '1px solid #eceff1',
  flexShrink: 0,
});

export const SummaryText = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '12px',
  fontWeight: 600,
  color: '#737373',
  whiteSpace: 'nowrap',
});

export const SummaryStrong = styled('span')({ color: '#262626' });
export const SummaryMuted = styled('span')({ color: '#a6a6a6' });

export const FooterActions = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
});

export const CancelButton = styled(Box)({
  width: '84px',
  height: '38px',
  borderRadius: '7px',
  backgroundColor: '#d9d9d9',
  color: '#262626',
  fontSize: '14px',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'background-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': { backgroundColor: '#cfcfcf' },
  '&:active': { transform: 'scale(0.97)' },
});

export const ApplyButton = styled(Box)({
  minWidth: '130px',
  padding: '0 16px',
  height: '38px',
  borderRadius: '7px',
  backgroundColor: '#4ab3bc',
  color: '#fff',
  fontSize: '14px',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'background-color 150ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1)',
  '&:hover': { backgroundColor: '#3a9fa8' },
  '&:active': { transform: 'scale(0.97)' },
});



// 상품 필터 — 종류 드롭박스 + 상품 선택, 고른 것은 아래에 태그로 쌓인다.
export const ProductPickRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
});

export const ProductKindSelectSx = {
  // 화살표 아이콘(24px)이 글자 위를 덮어 '개인'이 잘려 보였다. 폭과 오른쪽 여백을 함께 잡는다.
  width: '104px',
  flexShrink: 0,
  height: '36px',
  borderRadius: '8px',
  backgroundColor: '#fff',
  fontSize: '13px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D9D9D9' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4ab3bc' },
  '& .MuiSelect-select': {
    padding: '8px 32px 8px 12px !important',
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '20px',
    minHeight: 'unset',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .MuiSelect-icon': { right: '8px' },
};

export const ProductInputSx = {
  '& .MuiOutlinedInput-root': {
    height: '36px',
    padding: '0 32px 0 12px !important',
    borderRadius: '8px',
    backgroundColor: '#fff',
    fontSize: '13px',
    '& fieldset': { borderColor: '#D9D9D9' },
    '&:hover fieldset': { borderColor: '#4ab3bc' },
  },
  '& .MuiOutlinedInput-input': { padding: '0 !important', height: '34px' },
};

export const ProductTagRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
  marginTop: '8px',
});

export const ProductTag = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  maxWidth: '100%',
  height: '26px',
  padding: '0 6px 0 8px',
  borderRadius: '13px',
  border: '1px solid #4ab3bc',
  backgroundColor: '#E4F5F6',
  fontSize: '12px',
  color: '#0F7F85',
});

export const ProductTagKind = styled('span')({
  flexShrink: 0,
  fontSize: '10.5px',
  fontWeight: 700,
  color: '#0F7F85',
  opacity: 0.75,
});

export const ProductTagLabel = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontWeight: 600,
});

export const ProductTagRemove = styled('span')({
  flexShrink: 0,
  cursor: 'pointer',
  width: '16px',
  height: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  fontSize: '10px',
  color: '#0F7F85',
  '&:hover': { backgroundColor: 'rgba(15,127,133,0.16)' },
});

// 제목 옆에 붙는 보조문구. 줄을 따로 쓰면 폭을 꽉 채워 문장이 늘어져 읽히지 않는다.
export const FilterSectionHint = styled('span')({
  marginLeft: '6px',
  fontSize: '11px',
  fontWeight: 500,
  letterSpacing: 0,
  color: '#A2ABB2',
});

// 복수 선택했을 때만 나오는 규칙 안내. 태그 바로 위에 둬야 무엇에 대한 말인지 붙어 읽힌다.
export const ProductMultiHint = styled(Box)({
  marginTop: '8px',
  fontSize: '11px',
  color: '#0F7F85',
});
